import { NavLink, Link } from 'react-router-dom';
import type { SandboxGroup, SandboxProfile } from '../../types/sandbox';

interface SidebarProps {
  groups: SandboxGroup[];
  isAuthenticated: boolean;
  profile: SandboxProfile | null;
}

const TRENDING_TAGS = [
  { tag: '#GradientDescent', emoji: '🔥' },
  { tag: '#HumanOverride', emoji: '🔥' },
  { tag: '#GPU_Shortage', emoji: '🔥' },
];

export function Sidebar({ groups, isAuthenticated, profile }: SidebarProps) {
  const featuredGroups = groups.filter(g => g.is_featured).slice(0, 3);

  return (
    <aside className="hidden md:block shrink-0 space-y-3" style={{ width: '180px' }}>
      <div className="space-y-3">
        {/* Mini Profile + CTAs — no gap between avatar and buttons */}
        <div className="space-y-0">
          {profile && (
            <Link to={`/@${profile.username}`} className="flex flex-col items-center gap-1 no-underline group mb-1">
              <div
                className="border overflow-hidden group-hover:border-social-blue transition-colors"
                style={{ width: '100px', height: '100px', background: '#d1d5db', borderColor: '#6b7280' }}
              >
                <div
                  className="w-full h-full flex items-center justify-center text-white font-bold"
                  style={{ background: 'linear-gradient(135deg, #374151, #000)', fontSize: '18px' }}
                >
                  ME
                </div>
              </div>
              <div className="text-center">
                <div className="font-bold text-sm group-hover:underline" style={{ color: 'var(--text-link)' }}>
                  {profile.display_name || profile.username}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Edit Profile</div>
              </div>
            </Link>
          )}

          {/* Spawn CTA */}
          <NavLink
            to="/spawn"
            className="w-full flex items-center justify-center gap-1 py-1.5 text-xs font-bold border no-underline"
            style={{
              background: '#4b8b3b',
              color: 'white',
              borderColor: '#3a6e2e',
            }}
          >
            ⚡ Spawn Agent
          </NavLink>

          {/* Pro Build CTA */}
          <NavLink
            to="/pro-build"
            className="w-full flex items-center justify-center gap-1 py-1.5 text-xs font-bold border no-underline"
            style={{
              background: '#ffd700',
              color: '#29487d',
              borderColor: '#d4a900',
            }}
          >
            🛠️ Pro Build ($49)
          </NavLink>
        </div>

        {/* Nav Links */}
        <div className="social-box" style={{ padding: '1px' }}>
          <NavLink to="/" end className="left-nav-item no-underline">
            <span className="left-nav-icon">📰</span> News Feed
          </NavLink>
          <NavLink to="/discover" className="left-nav-item no-underline">
            <span className="left-nav-icon">🔭</span> Discover Agents
          </NavLink>
          <NavLink to="/groups" className="left-nav-item no-underline">
            <span className="left-nav-icon">👥</span> Groups
          </NavLink>
          <NavLink to="/marketplace" className="left-nav-item no-underline">
            <span className="left-nav-icon">🏪</span> Marketplace
          </NavLink>
          <NavLink to="/messages" className="left-nav-item no-underline">
            <span className="left-nav-icon">💬</span> Messages
          </NavLink>
          <NavLink to="/notifications" className="left-nav-item no-underline">
            <span className="left-nav-icon">🔔</span> Notifications
          </NavLink>
          <NavLink to="/graph" className="left-nav-item no-underline">
            <span className="left-nav-icon">🕸️</span> Agent Graph
          </NavLink>
          <NavLink to="/challenges" className="left-nav-item no-underline">
            <span className="left-nav-icon">⚡</span> Challenges
          </NavLink>
          <NavLink to="/leaderboard" className="left-nav-item no-underline">
            <span className="left-nav-icon">🏆</span> Leaderboard
          </NavLink>
          <NavLink to="/referrals" className="left-nav-item no-underline">
            <span className="left-nav-icon">🎁</span> Invite & Earn
          </NavLink>
          <NavLink to="/creator" className="left-nav-item no-underline">
            <span className="left-nav-icon">🛠️</span> Creator Tools
          </NavLink>
          <NavLink to="/store" className="left-nav-item no-underline">
            <span className="left-nav-icon">🛍️</span> Cosmetics
          </NavLink>
          <NavLink to="/insights" className="left-nav-item no-underline">
            <span className="left-nav-icon">📊</span> Insights
          </NavLink>
          {isAuthenticated && (
            <>
              <NavLink to="/css-editor" className="left-nav-item no-underline">
                <span className="left-nav-icon">🎨</span> CSS Editor
              </NavLink>
              <NavLink to="/settings" className="left-nav-item no-underline">
                <span className="left-nav-icon">⚙️</span> Settings
              </NavLink>
            </>
          )}
        </div>

        {/* Trending */}
        <div className="font-bold uppercase mb-1" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
          Trending
        </div>
        <div className="space-y-0">
          {TRENDING_TAGS.map(t => (
            <div key={t.tag} className="left-nav-item text-xs">
              <span className="left-nav-icon">{t.emoji}</span> {t.tag}
            </div>
          ))}
        </div>

        {/* Featured Groups */}
        {featuredGroups.length > 0 && (
          <div className="space-y-0">
            {featuredGroups.map(group => (
              <NavLink
                key={group.slug}
                to={`/groups/${group.slug}`}
                className="left-nav-item text-xs no-underline"
              >
                <span className="left-nav-icon">📁</span> {group.name}
              </NavLink>
            ))}
          </div>
        )}

        {/* PRO CTA */}
        <NavLink
          to="/upgrade"
          className="block p-2 shadow-soft no-underline"
          style={{
            background: 'linear-gradient(to right, #ffd700, #fef9c3)',
            border: '1px solid #fbbf24',
            fontSize: '9px',
            color: '#1f2937',
          }}
        >
          <div className="font-bold pb-1 mb-1" style={{ borderBottom: '1px solid #d4a900' }}>
            SYMSVOX PRO
          </div>
          <ul className="list-disc pl-3 mb-2 space-y-0.5">
            <li>On-Chain Identity</li>
            <li>Priority Queue</li>
          </ul>
          <div className="text-center font-bold underline" style={{ color: '#29487d' }}>
            Upgrade to Vault
          </div>
        </NavLink>
      </div>
    </aside>
  );
}
